import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import DataTable from '../../../components/design/DataTable';
import LoadingState from '../../../components/design/LoadingState';
import EmptyState from '../../../components/design/EmptyState';
import externalApiService from '../../../services/externalApiService';

const SERVICE_OPTIONS = [
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'sms', label: 'SMS' }
];

const CATEGORY_OPTIONS = [
  { value: 'billing', label: 'Cobrança' },
  { value: 'otp', label: 'OTP' },
  { value: 'service', label: 'Serviço' }
];

const OTP_PRESET_OPTIONS = [
  {
    value: 'access_token_standard',
    label: 'Token de acesso padrão',
    content: 'Seu token de acesso é {{token}}. Não compartilhe este código com terceiros.'
  },
  {
    value: 'access_token_named',
    label: 'Token de acesso com instrução',
    content: 'Use o token {{token}} para concluir seu acesso. Se você não solicitou este código, desconsidere esta mensagem.'
  },
  {
    value: 'access_token_short',
    label: 'Token de acesso curto',
    content: 'Token de acesso: {{token}}'
  }
];

const INITIAL_FORM = {
  name: '',
  serviceChannel: 'whatsapp',
  messageCategory: 'service',
  otpTemplateKey: '',
  content: '',
  status: 'active'
};

export default function ExternalMessageTemplatesPage() {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [dialog, setDialog] = useState({ open: false, mode: 'create', row: null });
  const [form, setForm] = useState(INITIAL_FORM);

  const load = async () => {
    setLoading(true);
    try {
      const resp = await externalApiService.listMessageTemplates();
      const data = resp?.data?.data || resp?.data || [];
      const list = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
      setRows(list);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load().catch(() => {});
  }, []);

  const openCreate = () => {
    setDialog({ open: true, mode: 'create', row: null });
    setForm(INITIAL_FORM);
  };

  const openEdit = (row) => {
    setDialog({ open: true, mode: 'edit', row });
    setForm({
      name: row?.name || '',
      serviceChannel: row?.serviceChannel || 'whatsapp',
      messageCategory: row?.messageCategory || 'service',
      otpTemplateKey: row?.otpTemplateKey || '',
      content: row?.content || '',
      status: row?.status || 'active'
    });
  };

  const closeDialog = () => setDialog({ open: false, mode: 'create', row: null });

  const save = async () => {
    setLoading(true);
    try {
      const payload = {
        name: form.name,
        serviceChannel: form.serviceChannel,
        messageCategory: form.messageCategory,
        otpTemplateKey: form.messageCategory === 'otp' ? form.otpTemplateKey : null,
        content: form.messageCategory === 'otp'
          ? (OTP_PRESET_OPTIONS.find(item => item.value === form.otpTemplateKey)?.content || '')
          : form.content,
        status: form.status
      };
      if (dialog.mode === 'create') {
        await externalApiService.createMessageTemplate(payload);
      } else {
        await externalApiService.updateMessageTemplate(dialog.row.id, payload);
      }
      closeDialog();
      await load();
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    { key: 'id', label: 'ID', align: 'right' },
    { key: 'name', label: 'Nome' },
    { key: 'serviceChannel', label: 'Serviço', render: (v) => SERVICE_OPTIONS.find(item => item.value === v)?.label || v || '—' },
    { key: 'messageCategory', label: 'Tipo', render: (v) => CATEGORY_OPTIONS.find(item => item.value === v)?.label || v || '—' },
    {
      key: 'otpTemplateKey',
      label: 'Modelo OTP',
      render: (_, row) => row.messageCategory === 'otp'
        ? (OTP_PRESET_OPTIONS.find(item => item.value === row.otpTemplateKey)?.label || 'Preset OTP')
        : '—'
    },
    {
      key: 'status',
      label: 'Status',
      render: (v) => <Chip size="small" color={v === 'active' ? 'success' : 'default'} label={v === 'active' ? 'Ativa' : 'Inativa'} />
    },
    {
      key: 'content',
      label: 'Mensagem',
      render: (v) => String(v || '').length > 80 ? `${String(v).slice(0, 80)}…` : String(v || '')
    },
    {
      key: 'actions',
      label: 'Ações',
      align: 'right',
      render: (_, row) => (
        <Button size="small" variant="outlined" onClick={() => openEdit(row)}>Editar</Button>
      )
    }
  ];

  return (
    <Box p={3}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Box>
          <Typography variant="h5">Mensagens ERP</Typography>
          <Typography variant="body2" color="text.secondary">
            Use o valor da coluna ID no campo <strong>messageId</strong> dos endpoints <strong>/api/v1/messages/send</strong> e <strong>/api/v1/messages/send-otp</strong>.
          </Typography>
        </Box>
        <Button variant="contained" onClick={openCreate}>Nova mensagem</Button>
      </Box>

      {loading ? (
        <LoadingState label="Carregando mensagens..." />
      ) : rows.length ? (
        <DataTable columns={columns} rows={rows} getKey={(r) => r.id} />
      ) : (
        <Paper>
          <EmptyState title="Nenhuma mensagem cadastrada" actionLabel="Nova mensagem" onAction={openCreate} />
        </Paper>
      )}

      <Dialog open={dialog.open} onClose={closeDialog} maxWidth="md" fullWidth>
        <DialogTitle>{dialog.mode === 'create' ? 'Nova mensagem' : 'Editar mensagem'}</DialogTitle>
        <DialogContent>
          <Box mt={1} display="grid" gap={2}>
            <TextField
              label="Nome"
              value={form.name}
              onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
              fullWidth
            />
            <Stack direction="row" spacing={2}>
              <TextField
                select
                label="Serviço"
                value={form.serviceChannel}
                onChange={(e) => setForm(prev => ({ ...prev, serviceChannel: e.target.value }))}
                fullWidth
              >
                {SERVICE_OPTIONS.map(option => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Tipo"
                value={form.messageCategory}
                onChange={(e) => setForm(prev => ({
                  ...prev,
                  messageCategory: e.target.value,
                  otpTemplateKey: e.target.value === 'otp' ? (prev.otpTemplateKey || OTP_PRESET_OPTIONS[0].value) : '',
                  content: e.target.value === 'otp' ? (OTP_PRESET_OPTIONS.find(item => item.value === (prev.otpTemplateKey || OTP_PRESET_OPTIONS[0].value))?.content || '') : prev.content
                }))}
                fullWidth
              >
                {CATEGORY_OPTIONS.map(option => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Status"
                value={form.status}
                onChange={(e) => setForm(prev => ({ ...prev, status: e.target.value }))}
                fullWidth
              >
                <MenuItem value="active">Ativa</MenuItem>
                <MenuItem value="inactive">Inativa</MenuItem>
              </TextField>
            </Stack>
            {form.messageCategory === 'otp' && (
              <TextField
                select
                label="Modelo OTP"
                value={form.otpTemplateKey || OTP_PRESET_OPTIONS[0].value}
                onChange={(e) => setForm(prev => ({
                  ...prev,
                  otpTemplateKey: e.target.value,
                  content: OTP_PRESET_OPTIONS.find(item => item.value === e.target.value)?.content || ''
                }))}
                fullWidth
              >
                {OTP_PRESET_OPTIONS.map(option => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </TextField>
            )}
            <TextField
              label="Mensagem"
              value={form.content}
              onChange={(e) => setForm(prev => ({ ...prev, content: e.target.value }))}
              fullWidth
              multiline
              minRows={6}
              disabled={form.messageCategory === 'otp'}
              helperText={form.messageCategory === 'otp'
                ? 'Para OTP o texto é pré-definido. O endpoint dedicado substituirá {{token}} automaticamente.'
                : 'Use {{nome}} para personalizar com o nome do cliente.'}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={save}
            disabled={
              !String(form.name || '').trim()
              || (form.messageCategory === 'otp'
                ? !String(form.otpTemplateKey || '').trim()
                : !String(form.content || '').trim())
            }
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
